import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Calendar, CheckCircle2, Clock, Info, Lock, Mail, MapPin, Phone, User, X } from 'lucide-react';
import { sendMensMorningRegistrationToMake } from '../utils/makeWebhook';
import { openTranzilaPayment } from '../utils/tranzila';
import { getUtmParams } from '../utils/utmTracking';
const EVENT_NAME = 'בוקר גברים על המים';
const EVENT_DATE = '06.11.2026';
const EVENT_DATE_LABEL = 'שישי, 06.11.2026';
const EVENT_TIME = '08:30 - 13:00';
const PRICE = 480;
const STORAGE_KEY = 'mensMorningOnWaterRegistration';
const SUCCESS_MESSAGE = 'mens-morning-payment-success';
interface RegistrationData {
  fullName: string;
  email: string;
  phone: string;
  selectedDate: string;
  dateOfBirth: string;
  noMedicalLimitations: boolean;
  hasExperience: string;
  notes: string;
}
interface StoredRegistration extends RegistrationData {
  event: string;
  eventDate: string;
  price: number;
  submittedAt: string;
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_content: string;
  utm_term: string;
  utm_id: string;
  fbclid: string;
}
interface MensMorningRegistrationProps {
  isFormOpen: boolean;
  onFormOpenChange: (open: boolean) => void;
  returnOnly?: boolean;
}
const INITIAL_FORM: RegistrationData = {
  fullName: '',
  email: '',
  phone: '',
  selectedDate: EVENT_DATE,
  dateOfBirth: '',
  noMedicalLimitations: false,
  hasExperience: '',
  notes: ''
};
function readStoredRegistration(): StoredRegistration | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) as StoredRegistration : null;
  } catch (error) {
    console.warn('Could not read Men’s Morning registration', error);
    return null;
  }
}
export function MensMorningRegistration({
  isFormOpen,
  onFormOpenChange,
  returnOnly = false
}: MensMorningRegistrationProps) {
  const [formData, setFormData] = useState<RegistrationData>(INITIAL_FORM);
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const paymentIframeRef = useRef<HTMLIFrameElement>(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isPaid = params.get('paid') === '1' || params.get('Response') === '000';
    if (!isPaid) return;
    const completeRegistration = async () => {
      const registration = readStoredRegistration();
      if (registration) {
        const fallbackUtm = getUtmParams();
        const wasSent = await sendMensMorningRegistrationToMake({
          type: 'Mens Morning On Water Registration',
          source: 'MagicPatterns - Mens Morning On Water',
          paid: true,
          event: EVENT_NAME,
          eventDate: EVENT_DATE,
          selectedDate: registration.selectedDate,
          fullName: registration.fullName,
          email: registration.email,
          phone: registration.phone,
          dateOfBirth: registration.dateOfBirth,
          noMedicalLimitations: registration.noMedicalLimitations,
          hasExperience: registration.hasExperience,
          notes: registration.notes,
          price: registration.price,
          submittedAt: registration.submittedAt,
          utm_source: registration.utm_source || fallbackUtm.utm_source,
          utm_medium: registration.utm_medium || fallbackUtm.utm_medium,
          utm_campaign: registration.utm_campaign || fallbackUtm.utm_campaign,
          utm_content: registration.utm_content || fallbackUtm.utm_content,
          utm_term: registration.utm_term || fallbackUtm.utm_term,
          utm_id: registration.utm_id || fallbackUtm.utm_id,
          fbclid: registration.fbclid || fallbackUtm.fbclid
        });
        if (wasSent) localStorage.removeItem(STORAGE_KEY);
      }
      if (window.parent !== window) {
        window.parent.postMessage({
          type: SUCCESS_MESSAGE,
          submittedAt: registration?.submittedAt
        }, window.location.origin);
      } else {
        setPaymentSuccess(true);
        setIsPaymentOpen(true);
        window.history.replaceState({}, '', window.location.pathname);
      }
    };
    void completeRegistration();
  }, []);
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || event.data?.type !== SUCCESS_MESSAGE) {
        return;
      }
      setPaymentSuccess(true);
      setIsSubmitting(false);
      const registration = readStoredRegistration();
      const submittedAt = registration?.submittedAt || event.data?.submittedAt || '';
      const dedupKey = `mm_purchase_fired_${submittedAt}`;
      const alreadyFired = submittedAt && localStorage.getItem(dedupKey);
      if (!alreadyFired && window.fbq) {
        window.fbq('trackCustom', 'mm_purchase', {
          content_name: EVENT_NAME,
          content_category: 'Mens Morning On Water',
          value: registration?.price ?? PRICE,
          currency: 'ILS'
        });
        if (submittedAt) localStorage.setItem(dedupKey, '1');
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);
  const closeForm = () => {
    if (!isSubmitting) onFormOpenChange(false);
  };
  const closePayment = () => {
    setIsPaymentOpen(false);
    setIsSubmitting(false);
    setPaymentSuccess(false);
  };
  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = event.target;
    setFormData(previous => ({
      ...previous,
      [name]: value
    }));
  };
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!formData.fullName.trim() || !formData.email.trim() || !formData.phone.trim() || !formData.dateOfBirth || !formData.noMedicalLimitations || !formData.hasExperience) {
      return;
    }
    setIsSubmitting(true);
    const utm = getUtmParams();
    const registration: StoredRegistration = {
      ...formData,
      event: EVENT_NAME,
      eventDate: EVENT_DATE,
      price: PRICE,
      submittedAt: new Date().toISOString(),
      ...utm
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(registration));
    if (window.fbq) {
      window.fbq('track', 'InitiateCheckout', {
        content_name: EVENT_NAME,
        content_category: 'Mens Morning On Water',
        value: PRICE,
        currency: 'ILS'
      });
    }
    onFormOpenChange(false);
    setIsPaymentOpen(true);
    setTimeout(() => {
      openTranzilaPayment({
        sum: PRICE,
        contact: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        pdesc: `${EVENT_NAME} (${EVENT_DATE})`,
        success_url: `${window.location.origin}/mens-morning-on-water?paid=1`,
        fail_url: window.location.href
      });
    }, 150);
  };
  if (returnOnly) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-700" dir="rtl" data-id="element-316" data-mp="p3byvxnimqgz">
        <div className="text-center" data-id="element-317" data-mp="aeo7m3kpd2wu">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full border-4 border-slate-200 border-t-amber-500 animate-spin" data-id="element-318" data-mp="afqxc86agzb6" />
          <p className="font-medium" data-id="element-319" data-mp="fdseuoo2gqkj">מאשרים את ההרשמה…</p>
        </div>
      </div>;
  }
  const inputClass = 'w-full bg-white border border-slate-300 rounded-xl py-3 pr-10 pl-4 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 disabled:opacity-60';
  return <>
      <AnimatePresence data-id="element-320" data-mp="y3h7uc8cxwx9">
        {isFormOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-950/70" onClick={closeForm} data-id="element-321" data-mp="y25t5hddj4rd">
            <motion.div role="dialog" aria-modal="true" aria-labelledby="mens-registration-title" initial={{
          opacity: 0,
          scale: 0.96,
          y: 16
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.96,
          y: 16
        }} transition={{
          duration: 0.22,
          ease: [0.23, 1, 0.32, 1]
        }} onClick={event => event.stopPropagation()} className="relative bg-slate-50 rounded-3xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-slate-200" dir="rtl" data-id="element-322" data-mp="301c6o6ddsj1s">
              <button type="button" onClick={closeForm} disabled={isSubmitting} className="absolute top-4 left-4 text-slate-400 hover:text-slate-700 p-2 z-10" aria-label="סגירה" data-id="element-323" data-mp="3531k1so2t8t1">
                <X className="w-5 h-5" data-id="element-324" data-mp="mgwwhkvht70" />
              </button>

              <div className="p-7 md:p-9" data-id="element-325" data-mp="1jcms87ia7h7u">
                <div className="text-center mb-6" data-id="element-326" data-mp="1jdo58hiuyds7">
                  <h2 id="mens-registration-title" className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" data-id="element-327" data-mp="1obfxxdrbu3xg">
                    הרשמה לבוקר גברים על המים
                  </h2>
                  <p className="text-slate-600 text-sm" data-id="element-328" data-mp="1xbco5jtd7aum">
                    משאירים פרטים וממשיכים לתשלום מאובטח
                  </p>
                  <p className="text-amber-700 font-bold mt-2" data-id="element-329" data-mp="229xe8qewke5c">
                    {EVENT_DATE_LABEL} · {EVENT_TIME} · ₪{PRICE}
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4" data-id="element-330" data-mp="2c11v5vo5292t">
                  <FormField icon={User} label="שם מלא" required data-id="element-331" data-mp="2g1c9pwpzy7ta">
                    <input type="text" name="fullName" value={formData.fullName} onChange={handleChange} required disabled={isSubmitting} autoComplete="name" placeholder="השם המלא שלך" className={inputClass} data-id="element-332" data-mp="2g3oo5zwjq01l" />
                  </FormField>
                  <FormField icon={Mail} label="כתובת מייל" required data-id="element-333" data-mp="2gjmwkd9nxhzy">
                    <input type="email" name="email" value={formData.email} onChange={handleChange} required disabled={isSubmitting} autoComplete="email" dir="ltr" placeholder="name@example.com" className={`${inputClass} text-right`} data-id="element-334" data-mp="2gha77f1ks5cv" />
                  </FormField>
                  <FormField icon={Phone} label="מספר טלפון" required data-id="element-335" data-mp="2uc2u4yjsonth">
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required disabled={isSubmitting} autoComplete="tel" dir="ltr" placeholder="050-1234567" className={`${inputClass} text-right`} data-id="element-336" data-mp="2zae191ipgk80" />
                  </FormField>

                  <div data-id="element-337" data-mp="395xxjp16lkxa">
                    <span className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-338" data-mp="3d943swoanc3r">מועד הפעילות</span>
                    <div className="flex items-center justify-center gap-2 py-3 px-3 rounded-xl font-bold text-sm border-2 bg-amber-50 text-slate-800 border-amber-400" data-id="element-339" data-mp="1eu9cr689qo4z">
                      <Calendar className="w-4 h-4 text-amber-600" data-id="element-340" data-mp="1ix5zn94ud6eq" />
                      {EVENT_DATE_LABEL}, {EVENT_TIME}
                    </div>
                  </div>

                  <FormField icon={Calendar} label="תאריך לידה" required data-id="element-341" data-mp="16jsk2yuw93pn">
                    <input type="date" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleChange} required disabled={isSubmitting} dir="ltr" className={`${inputClass} text-right`} data-id="element-342" data-mp="11n22u87cs22n" />
                  </FormField>

                  <label className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer ${formData.noMedicalLimitations ? 'bg-amber-50 border-amber-400' : 'bg-white border-slate-200'}`} data-id="element-343" data-mp="1u9wmj2wcixf2">
                    <input type="checkbox" checked={formData.noMedicalLimitations} onChange={event => setFormData(previous => ({
                  ...previous,
                  noMedicalLimitations: event.target.checked
                }))} disabled={isSubmitting} required className="sr-only" data-id="element-344" data-mp="1l86mj8oeeq5o" />
                    <span className={`shrink-0 mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center ${formData.noMedicalLimitations ? 'bg-amber-500 border-amber-500' : 'bg-white border-slate-300'}`} data-id="element-345" data-mp="4v8aj2ro3hhk">
                      {formData.noMedicalLimitations && <CheckCircle2 className="w-4 h-4 text-white" strokeWidth={3} data-id="element-346" data-mp="bb8hc97dacwh" />}
                    </span>
                    <span className="text-sm font-semibold text-slate-800" data-id="element-347" data-mp="3tjplotapai04">
                      אני מאשר שאין לי מגבלות רפואיות המונעות השתתפות בפעילות סאפ ואמבטיית קרח <span className="text-red-600" data-id="element-348" data-mp="3jrjuvmw6q6mi">*</span>
                    </span>
                  </label>

                  <div data-id="element-349" data-mp="3ojbm7zn60gdp">
                    <span className="block text-sm font-semibold text-slate-700 mb-2" data-id="element-350" data-mp="3ojuv6quu7ufl">
                      האם יש לך ניסיון על סאפ? <span className="text-red-600" data-id="element-351" data-mp="39rsvb6srp1fk">*</span>
                    </span>
                    <div className="grid grid-cols-2 gap-3" data-id="element-352" data-mp="3eqnp13s9waeg">
                      {[{
                    value: 'yes',
                    label: 'כן'
                  }, {
                    value: 'no',
                    label: 'לא'
                  }].map(option => <button key={option.value} type="button" disabled={isSubmitting} onClick={() => setFormData(previous => ({
                    ...previous,
                    hasExperience: option.value
                  }))} className={`py-3 rounded-xl font-bold text-sm border-2 ${formData.hasExperience === option.value ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:border-amber-400'}`} data-id="element-353" data-mp="35r0p6mf6zcni">
                          {option.label}
                        </button>)}
                    </div>
                  </div>

                  <div data-id="element-354" data-mp="352rug5z8sfwm">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-355" data-mp="306b4r7ce9thf">הערות / רגישויות שחשוב שנכיר</label>
                    <textarea name="notes" value={formData.notes} onChange={handleChange} disabled={isSubmitting} rows={3} placeholder="אלרגיות, רגישויות תזונה וכל דבר שחשוב שנדע..." className="w-full bg-white border border-slate-300 rounded-xl py-3 px-4 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none" data-id="element-356" data-mp="3etw892d6lupv" />
                  </div>

                  <button type="submit" disabled={isSubmitting} className="w-full bg-slate-900 hover:bg-slate-800 disabled:opacity-70 text-white text-lg font-bold py-4 rounded-xl transition-colors shadow-lg" data-id="element-357" data-mp="1h857i7jfxbml">
                    {isSubmitting ? 'פותחים את חלון התשלום…' : 'המשך לתשלום'}
                  </button>
                  <p className="text-slate-500 text-xs text-center" data-id="element-358" data-mp="1uxp40vmfhcng">התשלום מאובטח ומבוצע דרך Tranzila</p>
                  <CancellationPolicy data-id="element-359" data-mp="y9sv8cx8flms" />
                </form>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      <AnimatePresence data-id="element-360" data-mp="179flc2kih50j">
        {isPaymentOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-950/70" onClick={closePayment} data-id="element-361" data-mp="177uryg9xns49">
            <motion.div initial={{
          opacity: 0,
          scale: 0.96,
          y: 16
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.96,
          y: 16
        }} transition={{
          duration: 0.22,
          ease: [0.23, 1, 0.32, 1]
        }} onClick={event => event.stopPropagation()} className="relative bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden border border-slate-200 flex flex-col" dir="rtl" data-id="element-362" data-mp="2io30qzfyjzod">
              <div className="bg-amber-50 px-6 py-4 border-b border-amber-100 flex items-center justify-between shrink-0" data-id="element-363" data-mp="2nzggf1u5n930">
                <div className="flex items-center gap-2 text-slate-700" data-id="element-364" data-mp="2f0v2xl3jik93">
                  {paymentSuccess ? <CheckCircle2 className="w-5 h-5 text-emerald-600" data-id="element-365" data-mp="2eyidynzd79mj" /> : <Lock className="w-4 h-4 text-amber-700" data-id="element-366" data-mp="2a10cfinofwok" />}
                  <span className="text-sm font-semibold" data-id="element-367" data-mp="252fczlg91h16">{paymentSuccess ? 'ההרשמה הושלמה' : 'תשלום מאובטח באמצעות Tranzila'}</span>
                </div>
                <button type="button" onClick={closePayment} className="text-slate-400 hover:text-slate-700 p-1.5" aria-label="סגירה" data-id="element-368" data-mp="212wpqj0ygtv6"><X className="w-5 h-5" data-id="element-369" data-mp="38zj8fov7m1z" /></button>
              </div>

              {paymentSuccess ? <div className="flex-1 bg-slate-50 px-8 py-12 text-center flex flex-col items-center justify-center" data-id="element-370" data-mp="3vda1amhnlqoz">
                  <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mb-6" data-id="element-371" data-mp="1xiwbnz35113s"><CheckCircle2 className="w-10 h-10 text-emerald-600" data-id="element-372" data-mp="1js17v17hxa72" /></div>
                  <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3" data-id="element-373" data-mp="1jpy7pocmq0om">ההרשמה הצליחה!</h2>
                  <p className="text-slate-600 mb-7" data-id="element-374" data-mp="1nr07c6agker4">קיבלנו את ההרשמה ואת התשלום. נחום ישלח פרטים נוספים בוואטסאפ בקרוב.</p>
                  <div className="bg-white border border-slate-200 rounded-2xl p-5 w-full max-w-sm mb-6 text-right space-y-2" data-id="element-375" data-mp="2gijyccdaqx3o">
                    <p className="flex items-center gap-2" data-id="element-376" data-mp="22hh9xsytxapl"><Calendar className="w-4 h-4 text-amber-600" data-id="element-377" data-mp="22kdbrsdssjjb" />{EVENT_DATE_LABEL}</p>
                    <p className="flex items-center gap-2" data-id="element-378" data-mp="27fivqlnn3o4r"><Clock className="w-4 h-4 text-amber-600" data-id="element-379" data-mp="r3dkn036saqv" />{EVENT_TIME}</p>
                    <p className="flex items-center gap-2" data-id="element-380" data-mp="r5ge5u3jsm0c"><MapPin className="w-4 h-4 text-amber-600" data-id="element-381" data-mp="w2gl07881ssi" />חוף מעגן, כנרת</p>
                  </div>
                  <button type="button" onClick={closePayment} className="bg-slate-900 hover:bg-slate-800 text-white font-bold px-8 py-3 rounded-xl transition-colors" data-id="element-382" data-mp="nk4yngzrbxl0">סיום</button>
                </div> : <>
                  <div className="flex-1 bg-slate-50 overflow-hidden" data-id="element-383" data-mp="xh9j9cdyfkzp">
                    <iframe ref={paymentIframeRef} name="tranzila" width="100%" height="600" allow="payment" className="w-full h-[600px] max-h-[78vh] bg-white" title="חלון תשלום מאובטח" data-id="element-384" data-mp="sksmxuwlq0bj" />
                  </div>
                  <div className="shrink-0 bg-amber-50 border-t border-amber-100 px-5 py-3" data-id="element-385" data-mp="3w2sz7m96wruy"><CancellationPolicy data-id="element-386" data-mp="eljg3luacik8" /></div>
                </>}
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}
interface FormFieldProps {
  icon: React.ComponentType<{
    className?: string;
  }>;
  label: string;
  required?: boolean;
  children: React.ReactNode;
}
function FormField({
  icon: Icon,
  label,
  required,
  children
}: FormFieldProps) {
  return <label className="block" data-id="element-387" data-mp="2rsgqpkbx1cuj">
      <span className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-388" data-mp="2rt9o3uoziq00">
        {label} {required && <span className="text-red-600" data-id="element-389" data-mp="2wscnozm7440l">*</span>}
      </span>
      <span className="relative block" data-id="element-390" data-mp="31obe0sr33w3n">
        <Icon className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-600 pointer-events-none" data-id="element-391" data-mp="31rfp8a2yvs3t" />
        {children}
      </span>
    </label>;
}
function CancellationPolicy() {
  return <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-right" data-id="element-392" data-mp="3ao6und3esjb2">
      <div className="flex items-center gap-1.5 mb-1 text-amber-800" data-id="element-393" data-mp="3fn1coctjy3u4"><Info className="w-3.5 h-3.5" data-id="element-394" data-mp="3kgvv7cxf8crm" /><span className="text-xs font-bold" data-id="element-395" data-mp="3kgmc89asn5vf">מדיניות ביטולים</span></div>
      <p className="text-slate-500 text-[11px] leading-relaxed" data-id="element-396" data-mp="3pdw0hlmg5bho">ביטול עד 3 ימים לפני - חיוב 25% · ביטול יום לפני - חיוב 50% · ביטול ב־24 השעות שלפני האירוע - ללא זיכוי.</p>
    </div>;
}